import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  DEFAULT_SEO,
  SITE,
  buildCanonical,
  buildTitle,
  getRouteSeo,
  type SeoConfig,
} from '../config/seo';
import { organizationJsonLd, websiteJsonLd } from '../utils/structuredData';

const JSON_LD_ID = 'seo-json-ld';

function upsertMeta(attribute: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let element = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!element) {
    element = document.createElement('link');
    element.rel = rel;
    document.head.appendChild(element);
  }
  element.href = href;
}

function upsertJsonLd(data: Record<string, unknown> | Record<string, unknown>[]) {
  let script = document.getElementById(JSON_LD_ID) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.id = JSON_LD_ID;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(Array.isArray(data) ? data : [data]);
}

function applySeo(config: SeoConfig) {
  const title = buildTitle(config.title ?? DEFAULT_SEO.title);
  const description = config.description ?? DEFAULT_SEO.description;
  const canonical = buildCanonical(config.path ?? '/');
  const image = config.image ?? SITE.defaultImage;
  const type = config.type ?? 'website';
  const robots = config.noindex ? 'noindex, nofollow' : 'index, follow';

  document.title = title;
  document.documentElement.lang = SITE.language;

  upsertMeta('name', 'description', description);
  upsertMeta('name', 'robots', robots);
  upsertMeta('name', 'author', SITE.legalName);
  upsertMeta('name', 'theme-color', '#1b4332');

  upsertLink('canonical', canonical);

  upsertMeta('property', 'og:title', title);
  upsertMeta('property', 'og:description', description);
  upsertMeta('property', 'og:type', type);
  upsertMeta('property', 'og:url', canonical);
  upsertMeta('property', 'og:image', image);
  upsertMeta('property', 'og:site_name', SITE.name);
  upsertMeta('property', 'og:locale', SITE.locale);

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', title);
  upsertMeta('name', 'twitter:description', description);
  upsertMeta('name', 'twitter:image', image);

  if (config.jsonLd) {
    upsertJsonLd(config.jsonLd);
  } else if (!config.noindex && (config.path === '/' || !config.path)) {
    upsertJsonLd([organizationJsonLd(), websiteJsonLd()]);
  } else if (!config.noindex) {
    upsertJsonLd(organizationJsonLd());
  } else {
    const script = document.getElementById(JSON_LD_ID);
    script?.remove();
  }
}

type SeoContextValue = {
  setPageSeo: (config: SeoConfig | null) => void;
};

const SeoContext = createContext<SeoContextValue | null>(null);

export function SeoProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [pageOverride, setPageOverride] = useState<SeoConfig | null>(null);

  const activeConfig = useMemo(
    () => ({
      ...getRouteSeo(pathname),
      ...pageOverride,
      path: pageOverride?.path ?? pathname,
    }),
    [pathname, pageOverride],
  );

  useEffect(() => {
    applySeo(activeConfig);
  }, [activeConfig]);

  useEffect(() => {
    setPageOverride(null);
  }, [pathname]);

  return (
    <SeoContext.Provider value={{ setPageSeo: setPageOverride }}>{children}</SeoContext.Provider>
  );
}

export function PageSeo(config: SeoConfig) {
  const context = useContext(SeoContext);
  if (!context) {
    throw new Error('PageSeo must be used within SeoProvider');
  }

  const { setPageSeo } = context;
  const { title, description, path, image, type, noindex, jsonLd } = config;
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    setPageSeo({ title, description, path, image, type, noindex, jsonLd });
    return () => setPageSeo(null);
  }, [title, description, path, image, type, noindex, jsonLdKey, jsonLd, setPageSeo]);

  return null;
}

export function usePageSeo(config: SeoConfig) {
  const context = useContext(SeoContext);
  if (!context) return;

  const { setPageSeo } = context;

  useEffect(() => {
    setPageSeo(config);
    return () => setPageSeo(null);
  }, [config, setPageSeo]);
}
